import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/**
 * TOMT ER «—», OG DET ER ALDRI ET ORD (QA-runde 12, J6).
 *
 * ================================================================================================
 * TO FUNN, ÉN KOMPONENT.
 *
 *  - **6d og 24f:** «Artister uten pris viser "~ 0 kr". Vis "—".» Null er ikke null kroner. Et beløp
 *    som ikke er satt og et beløp som ER null er to forskjellige opplysninger, og å vise dem likt
 *    er å lyve om den ene. Samme sak med en blank celle: den leser som «kolonnen gjelder ikke her».
 *  - **25b og 34b:** «"Ingen agent" står grått på hver rad», «"○ Ingen" og "Ikke tildelt" gjentas
 *    på nesten alle rader». Atle har mislikt mønsteret før — «NO ANSWER»-pillene. En plassholder
 *    med ord er like bred som en verdi og bærer mindre; tjue av dem under hverandre er en kolonne
 *    som roper om det den ikke vet.
 *
 * Den ene streken løser begge: den sier «ingenting her» på en tegnbredde, den leses som ingenting,
 * og øyet glir forbi den ned til de radene som faktisk har noe.
 *
 * ================================================================================================
 * DEN ER IKKE BARE EN STRENG, OG DET ER DERFOR DEN ER EN KOMPONENT.
 *
 * `'—'` sto skrevet førti steder i `features/` da dette ble talt opp, halvparten med
 * `<span className="text-subtle">` rundt og halvparten uten. Det gir to gråtoner for det samme.
 *
 * Og en skjermleser leser en bindestrek som «bindestrek», eller hopper over den. Den som navigerer
 * en tabell med tastaturet får «Kari Nordmann, bindestrek, bindestrek, bindestrek» i stedet for å
 * vite hvilken kolonne som manglet. Derfor har hver strek et navn som bare leses opp: «Ikke satt»,
 * eller det kallstedet sier — «Ingen agent» hører hjemme der og ikke på skjermen.
 */

export interface EmptyValueProps {
  /**
   * Hva som mangler, for den som ikke ser streken. «Ingen agent», «Ikke tildelt».
   *
   * Utelat den for det vanlige, og det blir «Ikke satt». Dette er det ENE stedet de ordene fra
   * 25b og 34b fortsatt hører hjemme: usynlig, én gang per celle, for den som trenger dem.
   */
  label?: string;
  className?: string;
}

export function EmptyValue({ label, className }: EmptyValueProps): ReactNode {
  const { t } = useTranslation();
  return (
    <>
      <span aria-hidden className={cn('text-subtle', className)}>
        {t('common.empty')}
      </span>
      <span className="sr-only">{label ?? t('common.emptyLabel')}</span>
    </>
  );
}

/**
 * Verdien, eller streken. Hjelperen tabellkolonner skrives med.
 *
 * `valueOr(row.agentName)` framfor `row.agentName ?? '—'`, og forskjellen er ikke kosmetisk: den
 * andre gir en bar streng uten gråtone og uten navn for skjermleseren, og det var den som sto
 * førti steder. NULL, UNDEFINED OG TOM STRENG teller som tomt; `0` og `false` gjør IKKE det — et
 * beløp på null kroner er en opplysning, og å skjule den bak en strek ville vært den motsatte
 * feilen av den J6 retter.
 */
export function valueOr(value: ReactNode, options: { label?: string } = {}): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue {...(options.label === undefined ? {} : { label: options.label })} />;
  }
  return value;
}
